/*
  ATD - Guía de tallas 1
  Botón que abre un modal (dialog nativo) con la tabla de medidas.

  Web component <atd-sizeguide1>:
   - Abre/cierra un <dialog> nativo con showModal(): focus-trap y ESC los da el
     browser gratis. Se agrega: bloqueo de scroll del fondo y cierre por
     click-fuera del panel y por botón X. Sin librerías, sin tocar core de Horizon.
   - Toggle cm ↔ pulgadas: las celdas guardan el valor en cm en data-cm; al
     cambiar a pulgadas se convierte (× 0.3937) en el cliente. Default según el
     setting (cm para México).
   - Respeta prefers-reduced-motion (CSS) y restaura el scroll al cerrar.
*/
(() => {
  if (customElements.get('atd-sizeguide1')) return;

  const CM_TO_IN = 0.3937;

  class AtdSizeGuide1 extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-atd-sg-open]');
      this.dialog = this.querySelector('[data-atd-sg-dialog]');
      this.panel = this.querySelector('[data-atd-sg-panel]');
      this.closeBtn = this.querySelector('[data-atd-sg-close]');
      this.unitBtns = Array.from(this.querySelectorAll('[data-atd-sg-unit]'));
      this.cells = Array.from(this.querySelectorAll('td[data-cm]'));
      this.scrollFrame = this.querySelector('[data-atd-sg-frame]');
      this.scrollBox = this.querySelector('[data-atd-sg-scroll]');

      if (!this.trigger || !this.dialog) return;

      this.unit = this.dataset.defaultUnit === 'in' ? 'in' : 'cm';
      this._scrollY = 0;
      this._onScroll = () => this.#updateOverflowHint();
      this._onResize = () => this.#updateOverflowHint();

      this.#wireDialog();
      this.#wireUnits();
      this.#wireScrollHint();

      // Estado inicial de las celdas según la unidad por defecto.
      this.#renderUnit();
    }

    disconnectedCallback() {
      if (this.scrollBox && this._onScroll) {
        this.scrollBox.removeEventListener('scroll', this._onScroll);
      }
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    /* ---------------- Modal abrir / cerrar ---------------- */
    #wireDialog() {
      this.trigger.addEventListener('click', () => this.#open());

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.#close());
      }

      // Click-fuera: si el click cae en el backdrop (el propio <dialog>, no el panel).
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.#close();
      });

      // ESC nativo del <dialog> dispara "cancel": lo interceptamos para correr
      // nuestro cierre (restaura scroll) y evitar el close() inmediato del browser.
      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.#close();
      });
    }

    #open() {
      if (this.dialog.open) return;
      this._scrollY = window.scrollY;

      this.dialog.showModal(); // focus-trap nativo

      // Bloqueo de scroll del fondo sin saltos de layout.
      document.body.style.top = `-${this._scrollY}px`;
      document.body.setAttribute('data-atd-sg-lock', '');

      // La tabla recién ahora es medible (el dialog estaba display:none): evaluar
      // si desborda para mostrar el degradado/peek. rAF para que el layout asiente.
      requestAnimationFrame(() => this.#updateOverflowHint());
    }

    #close() {
      if (!this.dialog.open) return;
      this.dialog.classList.add('is-closing');

      const finish = () => {
        this.dialog.classList.remove('is-closing');
        this.dialog.close();
        document.body.removeAttribute('data-atd-sg-lock');
        document.body.style.top = '';
        window.scrollTo({ top: this._scrollY, behavior: 'instant' });
        this.trigger.focus({ preventScroll: true });
      };

      // Respeta prefers-reduced-motion: sin animación, cierra directo.
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) {
        finish();
        return;
      }

      let done = false;
      const onEnd = () => {
        if (done) return;
        done = true;
        this.panel.removeEventListener('animationend', onEnd);
        finish();
      };
      this.panel.addEventListener('animationend', onEnd);
      // Fallback por si no dispara animationend.
      setTimeout(onEnd, 320);
    }

    /* ---------------- Toggle de unidades ---------------- */
    #wireUnits() {
      this.unitBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          const next = btn.dataset.atdSgUnit === 'in' ? 'in' : 'cm';
          if (next === this.unit) return;
          this.unit = next;
          this.#renderUnit();
          // El ancho de columnas cambia al convertir; re-evaluar overflow.
          this.#updateOverflowHint();
        });
      });
    }

    /* ---------------- Pista de scroll horizontal ---------------- */
    #wireScrollHint() {
      if (!this.scrollFrame || !this.scrollBox) return;

      this.scrollBox.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });

      // Marca "ya se deslizó" en cuanto el usuario interactúa (oculta la mini-pista
      // textual, pero el degradado sigue gobernado por el overflow real).
      const markScrolled = () => {
        this.scrollFrame.setAttribute('data-atd-sg-scrolled', '');
      };
      this.scrollBox.addEventListener('scroll', markScrolled, { once: true, passive: true });
      this.scrollBox.addEventListener('pointerdown', markScrolled, { once: true });
    }

    #updateOverflowHint() {
      if (!this.scrollFrame || !this.scrollBox) return;

      const { scrollLeft, scrollWidth, clientWidth } = this.scrollBox;
      // Margen de 2px para evitar parpadeo por redondeos sub-pixel.
      const hasMoreRight = scrollWidth - clientWidth - scrollLeft > 2;

      this.scrollFrame.setAttribute(
        'data-atd-sg-overflow',
        hasMoreRight ? 'right' : ''
      );
    }

    #renderUnit() {
      // Botones activos / aria-pressed.
      this.unitBtns.forEach((btn) => {
        const active = btn.dataset.atdSgUnit === this.unit;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      // Celdas: convierte desde el valor base en cm.
      this.cells.forEach((td) => {
        const cm = parseFloat(td.getAttribute('data-cm'));
        if (!Number.isFinite(cm) || cm <= 0) return; // celda vacía: deja el "—"
        if (this.unit === 'in') {
          td.textContent = this.#fmt(cm * CM_TO_IN);
        } else {
          td.textContent = this.#fmt(cm);
        }
      });
    }

    #fmt(n) {
      // Máx 1 decimal, sin ceros colgando (88 → "88", 34.6 → "34.6").
      const r = Math.round(n * 10) / 10;
      return Number.isInteger(r) ? String(r) : r.toFixed(1);
    }
  }

  customElements.define('atd-sizeguide1', AtdSizeGuide1);
})();
